import { Suspense, lazy } from "react"
import { Link, useLocation } from "@tanstack/react-router"
import { Logo } from "@/components/Logo"
import { authClient } from "@/lib/auth-client"
import { cn } from "@/lib/utils"
import { MobileMapToggle } from "@/components/MobileMapToggle"
import { useMapToggle } from "@/lib/contexts/map-toggle-context"
import { navLinks } from "@/components/header-nav"

// The plain header for everything that isn't a trip/guide page (home, explore,
// blog, dashboard, account, marketing). Trip/guide pages get EditorHeader; the
// two are chosen between in SiteHeader.
//
// Perf: the logo + plain nav links are all first paint needs, so the Radix-backed
// menus (Features dropdown, user/avatar menu, mobile nav) are lazy-loaded from
// HeaderMenus — that whole chunk (Radix DropdownMenu/Avatar + ProUpgradeDialog +
// icons) stays out of the marketing pages' first wave.
const FeaturesMenu = lazy(() =>
  import(`@/components/HeaderMenus`).then((m) => ({ default: m.FeaturesMenu }))
)
const UserMenu = lazy(() =>
  import(`@/components/HeaderMenus`).then((m) => ({ default: m.UserMenu }))
)
const MobileNav = lazy(() =>
  import(`@/components/HeaderMenus`).then((m) => ({ default: m.MobileNav }))
)

function HeaderNavLinks() {
  const location = useLocation()

  return (
    <nav className="hidden md:flex items-center gap-1">
      {navLinks.map((link) => {
        const isActive = location.pathname === link.to
        return (
          <Link
            key={link.label}
            to={link.to}
            // Intent-preload: don't drag the explore/guide route chunks onto the
            // marketing waterfall on scroll — fetch them on hover/touch instead.
            preload="intent"
            className={cn(
              `px-4 py-1.5 text-sm font-medium rounded-full transition-colors relative`,
              isActive
                ? `text-primary`
                : `text-muted-foreground hover:text-foreground`
            )}
          >
            {link.label}
            {isActive && (
              <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary rounded-full" />
            )}
          </Link>
        )
      })}
      {/* Its own Suspense so the plain links above paint immediately while the
          Radix Features dropdown streams in. */}
      <Suspense fallback={null}>
        <FeaturesMenu />
      </Suspense>
    </nav>
  )
}

// Dashboard sits next to the avatar (desktop only) rather than in the center
// nav — keeps the signed-in "go to my stuff" action grouped with account
// actions. Mobile still surfaces it inside MobileNav's dropdown.
function DashboardLink() {
  const location = useLocation()
  const { data: authSession } = authClient.useSession()
  if (!authSession?.user) return null

  const isActive = location.pathname === `/dashboard`
  return (
    <Link
      to="/dashboard"
      className={cn(
        `hidden md:inline-flex px-4 py-1.5 text-sm font-medium rounded-full transition-colors relative`,
        isActive ? `text-primary` : `text-muted-foreground hover:text-foreground`
      )}
    >
      Dashboard
      {isActive && (
        <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary rounded-full" />
      )}
    </Link>
  )
}

// The public guide-read view uses this plain Header (not EditorHeader — see
// is-trip-or-guide-page.ts). Its GuideShell publishes the mobile map/list toggle
// config via MapToggleContext (root-hosted, so it reaches this logged-out path
// too); render it here in the right action cluster, mobile-only.
function HeaderMapToggle() {
  const mapToggle = useMapToggle()
  const config = mapToggle?.config ?? null
  if (!config) return null
  return (
    <MobileMapToggle
      revealed={config.revealed}
      onToggle={config.onToggle}
      className="lg:hidden"
    />
  )
}

export default function Header() {
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-background backdrop-blur border-b border-border">
      <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 gap-6 h-13">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <Logo className="size-7 rounded-md object-contain" />
          <span className="text-base font-semibold tracking-tight">
            Chic<span className="text-primary">Travel</span>
          </span>
        </Link>

        {/* Nav links (desktop) */}
        <HeaderNavLinks />

        {/* Right actions */}
        <div className="flex items-center gap-3">
          <HeaderMapToggle />
          <Suspense fallback={null}>
            <MobileNav />
          </Suspense>
          <Suspense fallback={null}>
            <DashboardLink />
          </Suspense>
          <Suspense fallback={<div className="h-9 w-9" />}>
            <UserMenu />
          </Suspense>
        </div>
      </div>
    </header>
  )
}
